import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { InputAdornment, TextField } from '@mui/material'
import { de } from 'date-fns/locale'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import ExternalDatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import { IMaskInput } from 'react-imask'

import { getDateFromString } from '../../../utils/helper'
import Wrapper from '../DatePickerWrapper'

// MUI's inputComponent interface requires `ref` to be called with HTMLInputElement.
// IMaskInput uses `inputRef` for DOM access, so we need this adapter.
const MaskedInputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<typeof IMaskInput>
>(function MaskedInputComponent(props, ref) {
  return <IMaskInput {...props} inputRef={ref} />
})

interface DatePickerInputProps {
  mask: string
  label?: string
  fullWidth?: boolean
  helperText?: string
  error?: boolean
  disabled?: boolean
  adornmentLabel: React.ReactNode
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  placeholder?: string
  autoComplete?: string
  id?: string
}

const DatePickerInput = React.forwardRef<HTMLInputElement, DatePickerInputProps>(
  (
    {
      mask,
      label,
      fullWidth,
      helperText,
      error,
      disabled,
      adornmentLabel,
      value,
      onChange,
      onBlur,
      onClick,
      onFocus,
      onKeyDown,
      placeholder,
      autoComplete,
      id
    },
    ref
  ) => (
    <TextField
      value={value ?? ''}
      label={label}
      size="small"
      id={id}
      placeholder={placeholder}
      autoComplete={autoComplete}
      onClick={onClick as React.MouseEventHandler<HTMLDivElement>}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      sx={{
        '& .MuiOutlinedInput-input': {
          borderRight: '1px solid lightgrey'
        },
        '& + .react-datepicker__close-icon': {
          right: '40px !important',
          color: 'transparent !important'
        },
        '& + .react-datepicker__close-icon:hover:after': {
          backgroundImage: 'url(/assets/icons/clear_hover.svg)'
        },
        '& + .react-datepicker__close-icon::after': {
          content: '""',
          backgroundColor: 'transparent',
          backgroundImage: 'url(/assets/icons/clear.svg)',
          backgroundPosition: 'center center'
        }
      }}
      fullWidth={fullWidth}
      inputRef={ref}
      variant="outlined"
      slotProps={{
        input: {
          inputComponent: MaskedInputComponent as any,
          endAdornment: <InputAdornment position="end">{adornmentLabel}</InputAdornment>,
          inputProps: {
            mask,
            definitions: { '9': /[0-9]/ },
            lazy: false,
            onAccept: (val: string) => {
              if (onChange) {
                const e = { target: { value: val } } as React.ChangeEvent<HTMLInputElement>
                onChange(e)
              }
            }
          }
        }
      }}
      helperText={helperText}
      error={error}
      disabled={disabled}
      onBlur={onBlur}
    />
  )
)
DatePickerInput.displayName = 'DatePickerInput'

export interface DatePickerProps {
  onChange: (date: Date | null) => void
  type: 'date' | 'time' | 'datetime'
  value?: Date | null
  maxDate?: Date | null
  label?: string
  timeIntervals?: number
  timeCaption?: string
  fullWidth?: boolean
  disabled?: boolean
  helperText?: string
  error?: string
}

const DatePicker = ({
  value: propValue = null,
  onChange: onPropChange = () => {},
  label = 'Datum auswählen',
  type = 'date',
  timeIntervals = 15,
  timeCaption = 'Zeit',
  fullWidth = false,
  disabled = false,
  helperText = undefined,
  error,
  maxDate = undefined
}: DatePickerProps) => {
  const { t } = useTranslation('common')
  const [value, setValue] = useState(() => getDateFromString(propValue))
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
  }, [])

  useEffect(() => {
    setValue(getDateFromString(propValue))
  }, [propValue])

  const dateHandle = (date: Date | null): Date | null => {
    let ret = date
    if (type === 'date') {
      const mom = moment(date).set({ hours: 12 })
      if (mom.isValid()) ret = mom.toDate()
      else ret = null
    }

    return ret
  }

  const onChange = (date: Date) => {
    const handled = dateHandle(date)
    setValue(handled)
    // Skip propagation on initial mount: IMaskInput's onAccept fires with the
    // formatted value which strips seconds (format HH:mm), creating a Date that
    // differs from the original (which may include seconds). This would
    // incorrectly mark the react-hook-form field as dirty.
    if (typeof onPropChange === 'function' && mountedRef.current) {
      onPropChange(handled)
    }
  }
  const dateFormat = {
    date: 'dd.MM.yyyy',
    datetime: 'dd.MM.yyyy HH:mm',
    time: 'HH:mm'
  }
  const getMask = (maskType: string) => {
    let mask = '99.99.9999 99:99'
    if (maskType === 'date') mask = '99.99.9999'
    if (maskType === 'time') mask = '99:99'
    return mask
  }

  return (
    <Wrapper $fullWidth={fullWidth}>
      <ExternalDatePicker
        selected={value}
        onChange={onChange}
        locale={de}
        dateFormat={dateFormat[type]}
        showPopperArrow={false}
        showTimeSelect={type !== 'date'}
        showTimeSelectOnly={type === 'time'}
        timeFormat="HH:mm"
        timeIntervals={timeIntervals}
        timeCaption={timeCaption}
        maxDate={maxDate}
        isClearable={!disabled}
        disabled={disabled}
        customInput={
          <DatePickerInput
            mask={getMask(type)}
            label={label}
            fullWidth={fullWidth}
            helperText={helperText}
            error={!!error}
            disabled={disabled}
            adornmentLabel={type === 'time' ? t('oclock') : <CalendarTodayIcon fontSize="small" />}
          />
        }
      />
    </Wrapper>
  )
}
DatePicker.displayName = 'DatePicker'
export default DatePicker
